import { memo, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import platformService from "../../services/platform/platformService";
import marketDataService from "../../services/market/MarketDataService";
import {
  setSymbolsLoading,
  setSymbols,
  setSymbolsError,
  setOrderHistoryLoading,
  setOrderHistory,
  setOrderHistoryError,
  setInitialized,
  selectUser,
  selectIsLoggedIn,
  selectIsInitialized,
  setOpenOrdersLoading,
  setOpenOrders,
  setOpenOrdersError,
  setPendingOrdersLoading,
  setPendingOrders,
  setPendingOrdersError,
  setUser,
  setAccount,
  setPricesLoading,
  setPrices,
  setPricesError,
  setTimeframes,
} from "../../redux/features/platformSlice";
import { getStoredMarketWatchSymbols } from "../../utils/symbolPriceFilter";

const getItems = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const InitialRestRequest = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const user = useSelector(selectUser);
  const isInitialized = useSelector(selectIsInitialized);

  const isFetching = useRef(false);

  // =============== Get Account Details ===============
  const fetchAccountDetails = useCallback(async () => {
    if (!user) return;
    const res = await platformService.getDetails();
    if (res?.success) {
      dispatch(setUser(res.data?.user));
      dispatch(setAccount(res.data?.account));
    }
  }, [user, dispatch]);

  // =============== Fetch Symbol List ===============
  const fetchSymbolList = useCallback(async () => {
    try {
      dispatch(setSymbolsLoading(true));
      const res = await platformService.getSymbolList();
      if (res?.success) {
        const symbols = getItems(res);

        dispatch(setSymbols(symbols));
        marketDataService.setDynamicSymbols(symbols);

        const initialPrices = symbols.filter(
          symbol => Number.isFinite(Number(symbol?.bid)) && Number.isFinite(Number(symbol?.ask)),
        );
        if (initialPrices.length) {
          dispatch(setPrices(initialPrices));
          marketDataService.setInitialPrices(initialPrices);
        }
        return symbols;
      }
      dispatch(setSymbolsError(res?.message || "Failed to fetch symbol list"));
      return [];
    } catch (error) {
      console.error("Failed to fetch symbol list:", error);
      dispatch(
        setSymbolsError(error?.message || "Failed to fetch symbol list"),
      );
      return [];
    }
  }, [dispatch]);

  // ============= Fetch Open Orders ==================
  const fetchOpenOrders = useCallback(
    async () => {
      try {
        dispatch(setOpenOrdersLoading(true));
        const response = await platformService.getPositions();
        if (response?.success !== false && response?.status !== false) {
          dispatch(setOpenOrders(getItems(response)));
        } else {
          dispatch(setOpenOrdersError(response?.message || "Failed to fetch open positions"));
        }
      } catch (error) {
        console.error("Failed to fetch open orders:", error);
        dispatch(
          setOpenOrdersError(error?.message || "Failed to fetch open orders"),
        );
      }
    },
    [dispatch],
  );

  // ================ Fetch Pending Orders ===============
  const fetchPendingOrders = useCallback(
    async () => {
      try {
        dispatch(setPendingOrdersLoading(true));
        const response = await platformService.getPendingOrders();
        if (response?.success !== false && response?.status !== false) {
          dispatch(setPendingOrders(getItems(response)));
        } else {
          dispatch(setPendingOrdersError(response?.message || "Failed to fetch pending orders"));
        }
      } catch (error) {
        console.error("Failed to fetch pending orders:", error);
        dispatch(
          setPendingOrdersError(
            error?.message || "Failed to fetch pending orders",
          ),
        );
      }
    },
    [dispatch],
  );

  // ================ Fetch Order History ===============
  const fetchOrderHistory = useCallback(
    async () => {
      try {
        dispatch(setOrderHistoryLoading(true));
        const response = await platformService.getOrderHistory();
        if (response?.success !== false && response?.status !== false) {
          dispatch(setOrderHistory(getItems(response)));
        } else {
          dispatch(setOrderHistoryError(response?.message || "Failed to fetch order history"));
        }
      } catch (error) {
        console.error("Failed to fetch order history:", error);
        dispatch(
          setOrderHistoryError(
            error?.message || "Failed to fetch order history",
          ),
        );
      }
    },
    [dispatch],
  );

  // =============== Fetch last price for symbols ===============
  const fetchLastPrice = useCallback(
    async (symbols = []) => {
      try {
        dispatch(setPricesLoading(true));
        const response = await platformService.getLastPrice(symbols);
        if (response?.success) {
          const prices = Array.isArray(response.data) ? response.data : [];
          dispatch(setPrices(prices));
          marketDataService.setInitialPrices(prices);
        } else {
          dispatch(
            setPricesError(response?.message || "Failed to fetch last price"),
          );
        }
      } catch (error) {
        console.error("Failed to fetch last price:", error);
        dispatch(
          setPricesError(error?.message || "Failed to fetch last price"),
        );
      }
    },
    [dispatch],
  );

  const fetchTimeframes = useCallback(async () => {
    try {
      const response = await platformService.getTimeframes();
      const timeframes = getItems(response);
      if (timeframes.length) dispatch(setTimeframes(timeframes));
    } catch (error) {
      console.error("Failed to fetch chart timeframes:", error);
    }
  }, [dispatch]);

  const fetchInitialData = useCallback(async () => {
    if (!user || isFetching.current) return;

    isFetching.current = true;
    const symbolTask = fetchSymbolList();
    const tasks = [
      { name: "AccountDetails", task: fetchAccountDetails() },
      { name: "SymbolList", task: symbolTask },
      { name: "Timeframes", task: fetchTimeframes() },
      { name: "Positions", task: fetchOpenOrders() },
      { name: "PendingOrders", task: fetchPendingOrders() },
      { name: "OrderHistory", task: fetchOrderHistory() },
    ];

    await Promise.all(
      tasks.map((t) =>
        t.task.catch((err) => {
          console.error(`Initial data fetch failed for ${t.name}:`, err);
          return [];
        }),
      ),
    );

    await symbolTask.catch(() => []);
    const watchedSymbols = getStoredMarketWatchSymbols();
    if (watchedSymbols?.length) {
      await fetchLastPrice(watchedSymbols);
    }

    dispatch(setInitialized(true));
    isFetching.current = false;
  }, [
    user,
    dispatch,
    fetchAccountDetails,
    fetchSymbolList,
    fetchOpenOrders,
    fetchPendingOrders,
    fetchOrderHistory,
    fetchLastPrice,
    fetchTimeframes,
  ]);

  useEffect(() => {
    if (user && isLoggedIn && !isInitialized && !isFetching.current) {
      fetchInitialData();
    }
  }, [user, isLoggedIn, isInitialized, fetchInitialData]);

  return null;
};

export default memo(InitialRestRequest);
